import { Flex, Switch, Text } from '@chakra-ui/react';
import {
  FileUpload,
  FormFeedback,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { FieldLabel } from 'src/components/Field';
import { AxiosProgressEvent } from 'axios';
import { FormFieldFileUploadArray as IFormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import axiosInstance from 'src/api/client';
import { getFileExtension, parseUploadError } from 'src/utils/file';
import { get } from 'lodash';

import {
  getSignedPrivateUploadUrl
} from 'src/api/queries';
import { useState } from 'react';
import { IRecommendation } from '@publishing-engine/domain/models';
import FileViewerButtonText from '../FileViewerButtonText';

interface IFormFieldMultipleFileUploadProps {
  config: Omit<IFormFieldFileUploadArray, 'component'>;
  submissionContextId?: string;
  submissionId?: string;
  // temp fix for email attachments
  _usePublicUploads?: true;
  isEditingAllowed?: boolean;
  helperText?: string;
  width?: string;
  Recommendation: IRecommendation;
}

const MAX_FILES_AT_ONCE = 15;

type IFileUploaded = {
  fileId?: string;
  url: string;
  name: string;
  referenceName: string;
  isUploading: boolean;
  progress: number;
  extension: string;
  visibility?: string;
};
const RecommendationFormFieldMultipleFileUpload: React.FC<
  IFormFieldMultipleFileUploadProps
> = ({
  config,
  submissionId,
  submissionContextId,
  _usePublicUploads,
  isEditingAllowed = true,
  helperText,
  width = '100%'
}) => {
  const toast = useToast();
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    register
  } = useFormContext<{ [key: string]: IFileUploaded[] }>();

  const {
    caseDataFields: { main: field },
    params: { labels, validations }
  } = config;
  const error = errors[field];
  const mainError = get(errors, field);
  const [uploadRejectedFileError, setUploadRejectedFileError] = useState<
    string | null
  >(null);
  const { append, update, fields } = useFieldArray({
    control,
    name: field
  });

  const getUploadUrl = (extension: string) => {
    if (!submissionId) {
      throw new Error(
        'FormFieldMultipleFileUpload: no submission id. ' + field
      );
    }

    // if (_usePublicUploads)
    //   return getSignedUnprotectedUploadUrl(submissionId, extension);

    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const uploadFile = async (file: File, index: number) => {
    setUploadRejectedFileError('');
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: file.name,
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: '',
      visibility: 'show'
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };
      update(index, fileToUpload);
    };

    try {
      append(fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);
      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        referenceName: original_filename,
        extension: fileExtension
      };

      update(index, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);

      setError(`${field}.${index}`, {
        type: 'upload-error',
        message
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  // const removeFile = async (index: number, fileId?: string) => {
  //   remove(index);
  //   fileId && deleteUploadedFile(fileId);
  // };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);
    setUploadRejectedFileError('');
    const accepted = acceptedFiles;
    await Promise.all(
      accepted.map((file, index) => uploadFile(file, index + fields.length))
    );
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] =
    async rejectedFiles => {
      if (!rejectedFiles || rejectedFiles.length === 0) return;

      // Separate rejected files into 'too many files' and other errors
      const tooManyFiles = rejectedFiles.filter(r =>
        r.errors.some(e => e.code === 'too-many-files')
      );
      const otherRejected = rejectedFiles.filter(
        r => !r.errors.some(e => e.code === 'too-many-files')
      );

      // Upload only allowed number of files from 'too many files'
      if (tooManyFiles.length > 0 && MAX_FILES_AT_ONCE > 0) {
        const filesToUpload = tooManyFiles
          .slice(0, MAX_FILES_AT_ONCE)
          .map(r => r.file);

        await Promise.all(
          filesToUpload.map((file, index) =>
            uploadFile(file, index + fields.length)
          )
        );

        toast({
          variant: 'attention',
          title: 'File Upload Limit Exceeded',
          description:
            'You can only upload up to ' +
            MAX_FILES_AT_ONCE +
            ' files at once. Excess files were not uploaded.',
          isClosable: true
        });
      }

      // Handle other rejected files (wrong type, too large, etc.)
      if (otherRejected.length > 0) {
        const messages = otherRejected.map(({ file, errors }) => {
          const reasons = errors.map(err => {
            switch (err.code) {
              case 'file-invalid-type':
                return 'Unsupported file type';
              case ErrorCode.FileTooLarge:
                return `File is larger than ${validations.maxSizeMB}MB`;
              default:
                return 'Upload rejected';
            }
          });
          return `${file.name}: ${reasons.join(', ')}`;
        });

        setUploadRejectedFileError(messages.join(','));
      }
    };

  // const disableDeleteButton = () => {
  //   if (fields.length > 0) {
  //     return fields.some(file => file.isUploading);
  //   }
  //   return false;
  // };

  // const isDeleteButtonDisabled = disableDeleteButton();

  return (
    <Flex flexDirection="column" width="100%">
      {isEditingAllowed && (
        <Flex flexDirection="column" gap={2} marginBottom={2}>
          <Flex flexDirection="column">
            <FieldLabel
              isRequired={validations?.required}
              label={labels?.main}
              subText={labels?.subtitle}
              helpText={labels?.helpText}
            />
          </Flex>
          <Flex flexDirection="column" w={width}>
            <FileUpload
              {...register(field)}
              uploadButtonText="Select file(s)"
              multiple
              onDrop={onDrop}
              onDropRejected={onDropRejected}
              maxFiles={MAX_FILES_AT_ONCE}
              maxSize={validations?.maxSizeMB * 1000000}
              isInvalid={Boolean(error?.message)}
              accept={validations?.allowedFileFormats}
            />
          </Flex>
          <Text textStyle="textContext" alignSelf={helperText ? 'start' : ''}>
            {helperText && helperText !== ''
              ? `Accepted format(s): ${helperText}. Max size: ${validations.maxSizeMB}MB.`
              : `Max size: ${validations.maxSizeMB}MB. Executable scripts are not permitted.`}
          </Text>
        </Flex>
      )}
      {!isEditingAllowed && (
        <FieldLabel
          isRequired={validations?.required}
          label="Attached Files"
          subText=""
          helpText={labels?.helpText}
        />
      )}
      {fields?.length ? (
        <Flex flexDirection="column" gap={1}>
          {fields.map((file, index) => (
            <Flex
              key={file.id || file.url}
              flexDirection="column"
              _last={{ borderBottom: 'none' }}
              gap={2}
            >
              <Flex
                flexDirection="row"
                borderColor="neutralPrimaryBorder"
                width="100%"
              >
                {file.isUploading ? (
                  <Text textStyle="textContext">Uploading</Text>
                ) : (
                  <>
                    <Flex
                      width={'100%'}
                      mt={2}
                      py={2}
                      justifyContent="space-between"
                    >
                      <Flex gap={2} alignItems="center">
                        <FontAwesomeIcon icon={faFile} />
                        <Text textStyle="textBody">{file.name}</Text>
                        <FileViewerButtonText
                          file={file}
                          submissionContextId={submissionContextId}
                        >
                          View
                        </FileViewerButtonText> 
                      </Flex>
                      <Controller
                        {...register(`${field}.${index}.visibility`)}
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            isChecked={file.visibility === 'show'}
                            onChange={event => {
                              update(index, {
                                ...file,
                                visibility: event.target.checked
                                  ? 'show'
                                  : 'hide'
                              });
                            }}
                          />
                        )}
                      />
                    </Flex>
                  </>
                )}
              </Flex>
              {!file.isUploading && mainError?.[index] && (
                <FormFeedback variant="error">
                  {mainError?.[index]?.message}
                </FormFeedback>
              )}
            </Flex>
          ))}
        </Flex>
      ) : null}
      {uploadRejectedFileError ? (
        <Flex flexDirection="column" gap={2}>
          {(uploadRejectedFileError?.split(',') as string[]).map((msg, i) => (
            <FormFeedback key={i} variant="error">
              {msg}
            </FormFeedback>
          ))}
        </Flex>
      ) : (
        mainError?.type === 'manual' && (
          <FormFeedback variant="error">{mainError?.message}</FormFeedback>
        )
      )}
    </Flex>
  );
};

export default RecommendationFormFieldMultipleFileUpload;
